// @givmo/compliance — "Add a filing you make" (CCM-R1-INTAKE (a); frames 03, 04; copy ruling item
// 10 for a filing Givmo's library doesn't have).
//
// What the organization already knows, entered once: the filing, its number, and its last filing
// with the documents it has. Anything skipped can be added later, on the filing's panel, which
// opens as soon as the filing is added.
//
// ⚠ THE RECEIPT LAW HOLDS HERE AS ON THE PANEL: "Confirmed" adds only with the confirmation
// document attached; "Sent, not confirmed yet" is recorded as sent and is never a filing.
//
// ⚠ ONE WRITE PLACES THE FILING. The import mints and confirms the obligation and, with the
// confirmation, records the filing on it (the backend's own receipt rule, refused there too); a
// send is recorded after it. The filing as sent then goes where the server put the record, READ
// BACK (design go D1(2), `filedCopyHome`): that year's own door, or the obligation's for a duty
// made once; never the obligation's door for a recurring rule, which refuses it. The receipt kind
// is the mechanical map (D3), never a question: read from the rule's served deadlines (F4,
// CCM-R1-INTAKE-NEXTDUE): an import that lands on a recurring year records `renewed_filed`.
//
// ⚠ NOTHING HERE WORKS OUT A DATE. The picker's read serves each deadline's next date worked out for
// the organization's recorded facts (CCM-R1-INTAKE-NEXTDUE, frames 03 and 04), and the sheet shows
// exactly that (`compliance-add-sheet-model.ts`). A date the organization has not recorded yet (an
// unsaved fiscal year end, a typed licence expiry) is never reflected: the card says "Not worked
// out yet" until the backend works it out from what is recorded.

import { useEffect, useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  FOCUS_RING,
  Field,
  Icon,
  RadioTile,
  RadioTileGroup,
  Select,
  Sheet,
  Skeleton,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  addSheetNextDue,
  cardForTheEntry,
  recursOnYears,
  type AddSheetNextDue,
  type EnteredFilingRead,
} from './compliance-add-sheet-model';
import { useCompliance } from './compliance-context';
import { ComplianceDateField } from './compliance-date-field';
import {
  decodeProfile,
  decodeRegistrationImportBody,
  extractOccurrences,
  serverMessage,
  type ComplianceArtifact,
} from './compliance-decode';
import { ComplianceDisclosure } from './compliance-disclosure';
import {
  FILING_FAMILIES,
  FILING_FAMILY_NAMES,
  decodeOwnFilingBody,
  extractFilingOptions,
  extractRenewalCycles,
  type ComplianceFilingOption,
  type FilingFamily,
  type FilingYear,
  type OwnFilingCadence,
} from './compliance-filings-decode';
import { isListedLibraryFiling, lacksFiscalYearEnd } from './compliance-filings-model';
import { useFilingsComposition } from './compliance-filings-reads';
import {
  cycleTitle,
  DOCUMENT_ROLE_NAMES,
  filedCopyHome,
  LICENSE_ATTACHED_NOTE,
  receiptKindFor,
  recordedFiscalYearEnd,
  WHICH_YEAR_QUESTION,
} from './compliance-panel-model';
import {
  filingOptionsQueryKey,
  listAllFilingOptions,
  listAllOccurrences,
  listAllRenewalCycles,
} from './compliance-queries';
import { RuleProvenanceLine } from './compliance-shared';
import { AttachSlot, SheetFiscalYearEnd } from './filing-panel-forms';

const PREFIX = 'compliance-add-filing';

/**
 * What the sheet says when the filing as sent could not be placed: the filing is added, but no year
 * carries its record and it is no duty made once, so the copy stays in Documents, attached to
 * nothing (design go D1(2); Jonathan approved it as written, 2026-10-01, rulings log item 14).
 */
export const FILED_COPY_UNPLACED_COPY =
  'Filing added. The filing as sent is in Documents, not attached to a year.';

/** What an own filing's form says of it (copy ruling item 10). */
export const OWN_FILING_FORM_LINE =
  'Givmo’s library doesn’t have this filing, so its dates are the ones you enter.';

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-12 pt-8">
      <h3 className="text-s14 font-w600 text-text-strong">{title}</h3>
      {children}
    </section>
  );
}

function SheetTitle() {
  return (
    <Sheet.Header
      title="Add a filing you make"
      description="Tell us what you already know. Anything you skip can be added later."
    />
  );
}

function Footer({
  disabled,
  pending,
  onCancel,
}: {
  disabled: boolean;
  pending: boolean;
  onCancel: () => void;
}) {
  return (
    <Sheet.Footer>
      <Button appearance="saas" tone="secondary" type="button" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        appearance="saas"
        tone="primary"
        type="submit"
        disabled={disabled || pending}
        loading={pending}
        testId={`${PREFIX}-submit`}
      >
        Add filing
      </Button>
    </Sheet.Footer>
  );
}

// ── The "Next due" block (CCM-R1-INTAKE-NEXTDUE; frames 03, 04) ──────────────────────────────────

/** The block's grey card: the date or its words, the basis, the quiet second line, the source. */
function NextDueCard({ card }: { card: AddSheetNextDue['card'] }) {
  if (card.checking === true) {
    // (A), D-CCM-63: the design system's loading bars as REVIEW-27's 6A draws them, no words, the
    // source kept, and the card marked busy for screen readers; never the old date.
    return (
      <div
        className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12"
        aria-busy="true"
        {...testIdProp(`${PREFIX}-next-card`)}
      >
        <div className="flex h-24 items-center" {...testIdProp(`${PREFIX}-next-checking`)}>
          <Skeleton className="h-16 w-[148px]" />
        </div>
        {(card.basis !== undefined || card.aside !== undefined) && (
          <div className="flex flex-col gap-8 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </div>
        )}
        <div className="pt-4">
          <RuleProvenanceLine provenance={card.provenance} />
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12"
      {...testIdProp(`${PREFIX}-next-card`)}
    >
      <p
        className={cn(
          card.dated
            ? 'text-s16 font-w600 tabular-nums text-text-strong'
            : 'text-s14 font-w600 text-text-secondary',
        )}
        {...testIdProp(`${PREFIX}-next-lead`)}
      >
        {card.lead}
        {card.leadNote !== undefined && (
          <span
            className="text-s13 font-w400 text-text-secondary"
            {...testIdProp(`${PREFIX}-next-date-passed`)}
          >
            {' · '}
            {card.leadNote}
          </span>
        )}
      </p>
      {card.basis !== undefined && (
        <p className="text-s13 leading-h15 text-text-secondary" {...testIdProp(`${PREFIX}-basis`)}>
          {card.basis}
        </p>
      )}
      {card.aside !== undefined && (
        <p className="text-s12 leading-h15 text-text-tertiary" {...testIdProp(`${PREFIX}-aside`)}>
          {card.aside}
        </p>
      )}
      {/* The citation stays with what it cites: the card's last quiet line (frames 03, 04). */}
      <div className="pt-4">
        <RuleProvenanceLine provenance={card.provenance} />
      </div>
    </div>
  );
}

function NextDueBlock({
  nextDue,
  recordedFiscalYearEnd,
  anchorDate,
  onAnchorDate,
  licenseAttached,
  onFiscalYearEndUnsaved,
  yearEndAsks,
}: {
  nextDue: AddSheetNextDue;
  recordedFiscalYearEnd: string | undefined;
  anchorDate: string;
  onAnchorDate: (date: string) => void;
  licenseAttached: boolean;
  onFiscalYearEndUnsaved: (unsaved: boolean) => void;
  /** CCM-BE24-B: where moving the recorded year end asks whether the earlier one was wrong (Y1). */
  yearEndAsks?: { organization: string; today: string };
}) {
  return (
    <FormSection title={nextDue.heading}>
      <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-next-due`)}>
        {nextDue.lead !== undefined && (
          <p
            className="text-s13 leading-h15 text-text-secondary"
            {...testIdProp(`${PREFIX}-next-statement`)}
          >
            {nextDue.lead}
          </p>
        )}
        {nextDue.asksFiscalYearEnd && (
          <SheetFiscalYearEnd
            recorded={recordedFiscalYearEnd}
            onUnsavedChange={onFiscalYearEndUnsaved}
            {...(yearEndAsks !== undefined ? { asks: yearEndAsks } : {})}
          />
        )}
        {nextDue.asksAnchor !== undefined && (
          <div className="flex flex-col gap-6">
            <ComplianceDateField
              label={nextDue.asksAnchor.fieldLabel}
              value={anchorDate}
              onChange={onAnchorDate}
              testId={`${PREFIX}-anchor-date`}
            />
            {nextDue.licenseNoteApplies && licenseAttached && (
              <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-license-note`)}>
                {LICENSE_ATTACHED_NOTE}
              </p>
            )}
          </div>
        )}
        <NextDueCard card={nextDue.card} />
        {/* D-CCM-19 §3 row 7 (A-X1): one line under the worked-out date, at body size. */}
        {nextDue.sentence && <ComplianceDisclosure surface="add-filing" />}
      </div>
    </FormSection>
  );
}

// ── A filing in Givmo's library (frames 03, 04) ─────────────────────────────────────────────────

/** How long typing must stop before the card follows the date (REVIEW-27's 6: "about half a second"). */
export const ENTRY_SETTLE_MS = 500;

/**
 * CCM-R1-ADD-SHEET-NEXT-FROM-ENTRY (D-CCM-63): the entered date the card follows — the field's value
 * once typing has stopped for {@link ENTRY_SETTLE_MS}, or at once for a day picked from the calendar
 * (`settle`). A date only partly typed is no value, so it moves nothing.
 */
function useSettledDate(value: string): [string, (iso: string) => void] {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (value === settled) return undefined;
    const timer = setTimeout(() => setSettled(value), ENTRY_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [value, settled]);
  return [settled, setSettled];
}

/** A year's choice value: its key and the deadline it is a year of. */
function yearChoice(year: FilingYear): string {
  return `${year.cycleKey}|${year.deadlineIndex}`;
}

function LibraryFilingForm({
  onClose,
  onAdded,
  onNotListed,
  preset,
}: {
  onClose: () => void;
  onAdded: (filingId: string) => void;
  onNotListed: () => void;
  preset?: AddFilingPreset;
}) {
  const { orgRef, operations, organizationName } = useCompliance();
  const queryClient = useQueryClient();
  const ref = orgRef ?? '';
  const options = useQuery({
    queryKey: filingOptionsQueryKey(orgRef),
    queryFn: () => listAllFilingOptions(operations, ref),
    enabled: orgRef !== null,
  });
  // A filing already on the organization's list is recorded from its panel, not added again.
  const composition = useFilingsComposition(operations, orgRef);
  const listed = new Set(
    (composition.records?.obligations ?? [])
      .filter(isListedLibraryFiling)
      .map((obligation) => `${obligation.jurisdictionCode}|${obligation.ruleId}`),
  );
  const offered = extractFilingOptions(options.data).filter(
    (option) => !listed.has(`${option.jurisdictionCode}|${option.ruleId}`),
  );

  const [jurisdiction, setJurisdiction] = useState(preset?.jurisdictionCode ?? '');
  const [ruleId, setRuleId] = useState(preset?.ruleId ?? '');
  const [number, setNumber] = useState('');
  const [status, setStatus] = useState<'confirmed' | 'sent' | undefined>();
  const [date, setDate] = useState('');
  const [confirmation, setConfirmation] = useState<ComplianceArtifact | undefined>();
  const [filedCopy, setFiledCopy] = useState<ComplianceArtifact | undefined>();
  const [proof, setProof] = useState<ComplianceArtifact | undefined>();
  // CCM-R1-INTAKE-NEXTDUE: the date only the organization has (frame 04), sent with the import, and
  // whether the fiscal year end question holds a choice not yet saved (frame 03).
  const [anchorDate, setAnchorDate] = useState('');
  const [fiscalYearEndUnsaved, setFiscalYearEndUnsaved] = useState(false);
  // D-CCM-63: the card follows the entered last filing once its date settles, read again with it.
  const [settledDate, settle] = useSettledDate(status !== undefined ? date : '');
  const [yearChosen, setYearChosen] = useState('');

  const jurisdictions = [
    ...new Map(
      offered.map((option) => [
        option.jurisdictionCode,
        option.jurisdictionName || option.jurisdictionCode,
      ]),
    ),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const filings = offered.filter((option) => option.jurisdictionCode === jurisdiction);
  const option: ComplianceFilingOption | undefined = filings.find(
    (candidate) => candidate.ruleId === ruleId,
  );
  const jurisdictionName = option?.jurisdictionName || option?.jurisdictionCode || '';
  const profile = composition.records?.profile;
  const enteredOn = option !== undefined && settledDate !== '' ? settledDate : undefined;
  const entered = useQuery({
    queryKey: [...filingOptionsQueryKey(orgRef), 'entered', jurisdiction, enteredOn ?? ''],
    queryFn: () => listAllFilingOptions(operations, ref, jurisdiction, enteredOn),
    enabled: orgRef !== null && enteredOn !== undefined,
  });
  const enteredOption =
    enteredOn !== undefined
      ? extractFilingOptions(entered.data).find((candidate) => candidate.ruleId === ruleId)
      : undefined;
  const enteredRead: EnteredFilingRead =
    enteredOn === undefined
      ? 'none'
      : entered.isPending
        ? 'checking'
        : enteredOption === undefined
          ? 'failed'
          : 'read';
  // CCM-R1-ADD-SHEET-WHICH-YEAR (3a): where the date has no on-time reading, the years it could be
  // the late filing of; a year must be chosen before the filing is added. A choice the read no longer
  // offers (another date, another filing) counts as none.
  const years = enteredRead === 'read' ? (enteredOption?.lastFilingYears ?? []) : [];
  const chosenYear = years.find((year) => yearChoice(year) === yearChosen);
  const filedFor =
    chosenYear !== undefined
      ? { cycle_key: chosenYear.cycleKey, deadline_index: chosenYear.deadlineIndex }
      : undefined;
  // review-191 (F5): once a year is named, the card follows the filing on that year, read again with
  // it, as the filing will be recorded; the question's years stay the entered read's.
  const named = useQuery({
    queryKey: [
      ...filingOptionsQueryKey(orgRef),
      'entered',
      jurisdiction,
      enteredOn ?? '',
      chosenYear !== undefined ? yearChoice(chosenYear) : '',
    ],
    queryFn: () => listAllFilingOptions(operations, ref, jurisdiction, enteredOn, filedFor),
    enabled: orgRef !== null && enteredOn !== undefined && filedFor !== undefined,
  });
  const namedOption =
    filedFor !== undefined
      ? extractFilingOptions(named.data).find((candidate) => candidate.ruleId === ruleId)
      : undefined;
  const cardOption = filedFor !== undefined ? namedOption : enteredOption;
  const cardRead: EnteredFilingRead =
    filedFor === undefined
      ? enteredRead
      : named.isPending
        ? 'checking'
        : namedOption === undefined
          ? 'failed'
          : 'read';
  const baseNextDue =
    option !== undefined
      ? addSheetNextDue({
          option: cardRead === 'read' && cardOption !== undefined ? cardOption : option,
          profile,
          fiscalYearEndUnsaved,
          ...(composition.records?.today !== undefined ? { today: composition.records.today } : {}),
        })
      : undefined;
  const nextDue =
    baseNextDue !== undefined
      ? { ...baseNextDue, card: cardForTheEntry(baseNextDue.card, cardRead) }
      : undefined;
  const anchorAsked = nextDue?.asksAnchor;

  const add = useMutation({
    mutationFn: async () => {
      if (option === undefined) throw new Error('Choose a filing.');
      const trimmed = number.trim();
      const confirmed = status === 'confirmed' && confirmation !== undefined;
      const imported = decodeRegistrationImportBody(
        await operations.importRegistration(ref, {
          jurisdiction_code: option.jurisdictionCode,
          rule_id: option.ruleId,
          ...(trimmed.length > 0 ? { recorded_registration_number: trimmed } : {}),
          // Frame 04: the date the rule's deadline runs from, recorded with the filing.
          ...(anchorAsked !== undefined && anchorDate !== ''
            ? { [anchorAsked.records]: anchorDate }
            : {}),
          ...(confirmed
            ? {
                confirmation_artifact_id: confirmation.artifactId,
                recorded_filed_on: date,
                ...(filedFor !== undefined ? { filed_for: filedFor } : {}),
                // D3 from the rule's served deadlines (F4): a filing of a rule that recurs on years
                // lands on a renewal year; one that may be the first is a registration.
                filing_record_state: receiptKindFor(option.ruleClass, recursOnYears(option)),
              }
            : {}),
        }),
      );
      if (imported === undefined) throw new Error('We couldn’t add that filing. Please try again.');
      // The filing is placed. What follows goes onto it; a failure there leaves the filing in
      // place, so the panel still opens, to record it again.
      let after: unknown;
      let unplaced = false;
      try {
        if (status === 'sent') {
          await operations.recordSubmission(ref, imported.obligationId, {
            submission_recorded_on: date,
            ...(proof !== undefined ? { submission_proof_artifact_id: proof.artifactId } : {}),
            ...(filedFor !== undefined ? { filed_for: filedFor } : {}),
          });
        }
        if (status !== undefined && filedCopy !== undefined) {
          // D1(2): the filing as sent goes where the server put the record, read back — the year's
          // own door, or the obligation's for a duty made once; never a door that refuses it.
          const [cycleRows, occurrenceRows, profileBody] = await Promise.all([
            listAllRenewalCycles(operations, ref, imported.obligationId),
            listAllOccurrences(operations, ref),
            operations.getProfile(ref),
          ]);
          const home = filedCopyHome({
            obligationId: imported.obligationId,
            ...(filedFor !== undefined
              ? {
                  filedFor: {
                    cycleKey: filedFor.cycle_key,
                    deadlineIndex: filedFor.deadline_index,
                  },
                }
              : {}),
            status,
            date,
            cycles: extractRenewalCycles(cycleRows),
            occurrences: extractOccurrences(occurrenceRows),
            fiscalYearEndRecorded: !lacksFiscalYearEnd(decodeProfile(profileBody)),
          });
          if (home.kind === 'cycle') {
            await operations.attachRenewalFiledCopy(ref, home.cycleId, {
              artifact_id: filedCopy.artifactId,
            });
          } else if (home.kind === 'obligation') {
            await operations.attachObligationFiledCopy(ref, imported.obligationId, {
              artifact_id: filedCopy.artifactId,
            });
          } else {
            unplaced = true;
          }
        }
      } catch (error) {
        after = error;
      }
      return { obligationId: imported.obligationId, after, unplaced };
    },
    onSuccess: ({ obligationId, after, unplaced }) => {
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      if (after !== undefined) {
        toast.error(serverMessage(after, 'We couldn’t record that filing. Please try again.'));
      } else if (unplaced) {
        toast(FILED_COPY_UNPLACED_COPY);
      } else if (status === 'confirmed') {
        toast.success('Filing recorded.');
      } else if (status === 'sent') {
        toast.success('Recorded as sent.');
      }
      onAdded(obligationId);
    },
    onError: (error) =>
      toast.error(serverMessage(error, 'We couldn’t add that filing. Please try again.')),
  });

  // The receipt law: no confirmation document, no "Confirmed" add. And where the date has no on-time
  // reading, the year it was for, answered (CCM-R1-ADD-SHEET-WHICH-YEAR): the add waits for the read
  // with the date as entered, so the question is never passed before it can be asked.
  const lastFilingReady =
    status === undefined ||
    (date !== '' &&
      (status === 'sent' || confirmation !== undefined) &&
      settledDate === date &&
      enteredRead !== 'checking' &&
      (years.length === 0 || chosenYear !== undefined));
  // A fiscal year end chosen but not saved is not added with the filing: the year-end question's own
  // Save and Cancel settle it first, so the filing never lands on a year end the person just changed
  // (review-38's P1).
  const yearEndSettled = !(nextDue?.asksFiscalYearEnd === true && fiscalYearEndUnsaved);
  const ready = option !== undefined && lastFilingReady && yearEndSettled;

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) add.mutate();
      }}
    >
      <Sheet.Body className="flex flex-col gap-16">
        {preset?.label !== undefined && (
          <p className="text-s13 font-w600 text-text-secondary" {...testIdProp(`${PREFIX}-label`)}>
            {preset.label}
          </p>
        )}
        <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
          <Select
            appearance="saas"
            label="Jurisdiction"
            value={jurisdiction === '' ? null : jurisdiction}
            onValueChange={(next) => {
              setJurisdiction(next ?? '');
              setRuleId('');
              setAnchorDate('');
              // A year answered for one filing is never another's (nothing preselected).
              setYearChosen('');
            }}
            options={jurisdictions.map(([code, name]) => ({ value: code, label: name }))}
            testId={`${PREFIX}-jurisdiction`}
          />
          <Select
            appearance="saas"
            label="Filing"
            value={ruleId === '' ? null : ruleId}
            onValueChange={(next) => {
              setRuleId(next ?? '');
              setAnchorDate('');
              setYearChosen('');
            }}
            disabled={jurisdiction === ''}
            options={filings.map((candidate) => ({
              value: candidate.ruleId,
              label: candidate.displayTitle ?? candidate.ruleId,
            }))}
            testId={`${PREFIX}-filing`}
          />
        </div>
        <div>
          <button
            type="button"
            onClick={onNotListed}
            className={cn(
              'rounded-xs text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong',
              FOCUS_RING,
            )}
            {...testIdProp(`${PREFIX}-not-listed`)}
          >
            My filing isn’t listed
          </button>
        </div>
        {option !== undefined && (
          <>
            <Field
              label={option.numberLabel ?? 'Registration number'}
              value={number}
              maxLength={200}
              onChange={(event) => setNumber(event.target.value)}
              containerClassName="max-w-[240px]"
              testId={`${PREFIX}-number`}
            />
            <FormSection title="Your last filing">
              <RadioTileGroup
                value={status ?? ''}
                onValueChange={(next) => setStatus(next === 'sent' ? 'sent' : 'confirmed')}
                aria-label="Your last filing"
                testId={`${PREFIX}-status`}
              >
                <RadioTile
                  value="confirmed"
                  label="Confirmed"
                  description={`${jurisdictionName} sent back a confirmation, letter or license.`}
                />
                <RadioTile
                  value="sent"
                  label="Sent, not confirmed yet"
                  description="It’s recorded as sent. It isn’t marked filed until the confirmation is attached."
                />
              </RadioTileGroup>
              {status !== undefined && (
                <>
                  <ComplianceDateField
                    label={status === 'confirmed' ? 'Date filed' : 'Date sent'}
                    value={date}
                    onChange={setDate}
                    onPick={settle}
                    testId={`${PREFIX}-date`}
                  />
                  {years.length > 0 && (
                    <div className="flex flex-col gap-6" {...testIdProp(`${PREFIX}-which-year`)}>
                      <p className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary">
                        {WHICH_YEAR_QUESTION}
                      </p>
                      <RadioTileGroup
                        value={chosenYear !== undefined ? yearChoice(chosenYear) : ''}
                        onValueChange={setYearChosen}
                        aria-label={WHICH_YEAR_QUESTION}
                        className="flex flex-row flex-wrap gap-x-24 gap-y-8"
                        testId={`${PREFIX}-which-year-choices`}
                      >
                        {years.map((year) => {
                          const named = cycleTitle(year);
                          return (
                            <RadioTile
                              key={yearChoice(year)}
                              value={yearChoice(year)}
                              label={named.title}
                              {...(named.qualifier !== undefined
                                ? { description: named.qualifier }
                                : {})}
                            />
                          );
                        })}
                      </RadioTileGroup>
                    </div>
                  )}
                  <div className="flex flex-col items-start gap-8">
                    {status === 'confirmed' ? (
                      <>
                        <AttachSlot
                          button="Attach the confirmation"
                          label={DOCUMENT_ROLE_NAMES.confirmation}
                          artifact={confirmation}
                          onChange={setConfirmation}
                          testId={`${PREFIX}-confirmation`}
                        />
                        <AttachSlot
                          button="Attach the filing as sent (optional)"
                          label={DOCUMENT_ROLE_NAMES.filed_copy}
                          artifact={filedCopy}
                          onChange={setFiledCopy}
                          testId={`${PREFIX}-filed-copy`}
                        />
                      </>
                    ) : (
                      <>
                        <AttachSlot
                          button="Attach the filing as sent (optional)"
                          label={DOCUMENT_ROLE_NAMES.filed_copy}
                          artifact={filedCopy}
                          onChange={setFiledCopy}
                          testId={`${PREFIX}-filed-copy`}
                        />
                        <AttachSlot
                          button="Attach proof you sent it (optional)"
                          label={DOCUMENT_ROLE_NAMES.submission_proof}
                          artifact={proof}
                          onChange={setProof}
                          testId={`${PREFIX}-proof`}
                        />
                      </>
                    )}
                  </div>
                </>
              )}
            </FormSection>
            {nextDue !== undefined && (
              <NextDueBlock
                nextDue={nextDue}
                recordedFiscalYearEnd={recordedFiscalYearEnd(profile)}
                {...(profile?.yearEndChangeRequired === true &&
                composition.records?.today !== undefined
                  ? {
                      yearEndAsks: {
                        organization: organizationName ?? '',
                        today: composition.records.today,
                      },
                    }
                  : {})}
                anchorDate={anchorDate}
                onAnchorDate={setAnchorDate}
                licenseAttached={status === 'confirmed' && confirmation !== undefined}
                onFiscalYearEndUnsaved={setFiscalYearEndUnsaved}
              />
            )}
          </>
        )}
      </Sheet.Body>
      <Footer disabled={!ready} pending={add.isPending} onCancel={onClose} />
    </form>
  );
}

// ── A filing Givmo's library doesn't have (copy ruling item 10) ────────────────────────────────

type HowOften = 'one_time' | 'every_n_months' | 'every_n_years' | 'fixed_date_yearly';

const HOW_OFTEN: ReadonlyArray<{ value: HowOften; label: string }> = [
  { value: 'one_time', label: 'Once' },
  { value: 'every_n_months', label: 'Every few months' },
  { value: 'every_n_years', label: 'Every few years' },
  { value: 'fixed_date_yearly', label: 'On the same date each year' },
];

/** The interval bounds the backend's CHECK repeats (months 1–120, years 1–10). */
const INTERVAL_RANGE: Readonly<Record<'every_n_months' | 'every_n_years', [number, number]>> = {
  every_n_months: [1, 120],
  every_n_years: [1, 10],
};

function OwnFilingForm({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (filingId: string) => void;
}) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [agency, setAgency] = useState('');
  const [family, setFamily] = useState<FilingFamily | ''>('');
  const [howOften, setHowOften] = useState<HowOften | ''>('');
  const [interval, setInterval] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [number, setNumber] = useState('');

  const recurringEvery = howOften === 'every_n_months' || howOften === 'every_n_years';
  const intervalValue = Number(interval);
  const intervalOk =
    !recurringEvery ||
    (Number.isInteger(intervalValue) &&
      intervalValue >= INTERVAL_RANGE[howOften][0] &&
      intervalValue <= INTERVAL_RANGE[howOften][1]);
  const ready =
    title.trim().length > 0 && family !== '' && howOften !== '' && intervalOk && nextDate !== '';

  const add = useMutation({
    mutationFn: async () => {
      if (family === '' || howOften === '') throw new Error('Incomplete.');
      // A yearly filing's date each year is the next date's own month and day: asked once.
      const [, month, day] = nextDate.split('-').map(Number);
      const cadence: OwnFilingCadence = howOften;
      const filing = decodeOwnFilingBody(
        await operations.addOrganizationFiling(orgRef ?? '', {
          title: title.trim(),
          ...(agency.trim().length > 0 ? { agency: agency.trim() } : {}),
          filing_family: family,
          cadence,
          ...(recurringEvery ? { cadence_interval: intervalValue } : {}),
          ...(howOften === 'fixed_date_yearly' ? { cadence_month: month, cadence_day: day } : {}),
          next_due_on: nextDate,
          ...(number.trim().length > 0 ? { number: number.trim() } : {}),
        }),
      );
      if (filing === undefined) throw new Error('We couldn’t add that filing. Please try again.');
      return filing.filingId;
    },
    onSuccess: (filingId) => {
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      onAdded(filingId);
    },
    onError: (error) =>
      toast.error(serverMessage(error, 'We couldn’t add that filing. Please try again.')),
  });

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) add.mutate();
      }}
      {...testIdProp(`${PREFIX}-own`)}
    >
      <Sheet.Body className="flex flex-col gap-16">
        <p className="text-s13 leading-h15 text-text-secondary">{OWN_FILING_FORM_LINE}</p>
        <Field
          label="What it’s called"
          value={title}
          maxLength={200}
          autoFocus
          onChange={(event) => setTitle(event.target.value)}
          testId={`${PREFIX}-own-title`}
        />
        <Field
          label="Filed with (optional)"
          value={agency}
          maxLength={200}
          onChange={(event) => setAgency(event.target.value)}
          testId={`${PREFIX}-own-agency`}
        />
        <Select
          appearance="saas"
          label="Kind of filing"
          className="max-w-[320px]"
          value={family === '' ? null : family}
          onValueChange={(next) => setFamily((next as FilingFamily | null) ?? '')}
          options={FILING_FAMILIES.map((candidate) => ({
            value: candidate,
            label: FILING_FAMILY_NAMES[candidate],
          }))}
          testId={`${PREFIX}-own-family`}
        />
        <FormSection title="How often">
          <RadioTileGroup
            value={howOften}
            onValueChange={(next) => setHowOften(next as HowOften)}
            aria-label="How often"
            testId={`${PREFIX}-own-how-often`}
          >
            {HOW_OFTEN.map((choice) => (
              <RadioTile key={choice.value} value={choice.value} label={choice.label} />
            ))}
          </RadioTileGroup>
          {recurringEvery && (
            <div className="flex items-center gap-8 text-s14 text-text">
              <span id={`${PREFIX}-own-every`}>Every</span>
              <input
                type="number"
                inputMode="numeric"
                min={INTERVAL_RANGE[howOften][0]}
                max={INTERVAL_RANGE[howOften][1]}
                value={interval}
                onChange={(event) => setInterval(event.target.value)}
                aria-labelledby={`${PREFIX}-own-every ${PREFIX}-own-unit`}
                // The house field's control boundary (≥3:1, WCAG 1.4.11), as Field draws it.
                className={cn(
                  'h-36 w-[72px] rounded-md border bg-surface px-12',
                  'font-sans text-s14 font-w400 tracking-givmo text-text outline-none',
                  'transition-[box-shadow,border-color] border-neutral-500 hover:border-neutral-600',
                  'focus-visible:border-brand-500 focus-visible:shadow-ring',
                )}
                {...testIdProp(`${PREFIX}-own-interval`)}
              />
              <span id={`${PREFIX}-own-unit`}>
                {howOften === 'every_n_months' ? 'months' : 'years'}
              </span>
            </div>
          )}
        </FormSection>
        <ComplianceDateField
          label="Next date"
          value={nextDate}
          onChange={setNextDate}
          testId={`${PREFIX}-own-next`}
        />
        <Field
          label="Number (optional)"
          value={number}
          maxLength={100}
          onChange={(event) => setNumber(event.target.value)}
          containerClassName="max-w-[240px]"
          testId={`${PREFIX}-own-number`}
        />
      </Sheet.Body>
      <Footer disabled={!ready} pending={add.isPending} onCancel={onClose} />
    </form>
  );
}

/** The pages' header action that opens the sheet (frames 07, 09, 13). */
export function AddFilingButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      appearance="saas"
      tone="primary"
      size="sm"
      leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
      onClick={onClick}
      testId={`${PREFIX}-open`}
    >
      Add a filing
    </Button>
  );
}

// ── The sheet ──────────────────────────────────────────────────────────────────────────────────

export interface AddFilingSheetProps {
  open: boolean;
  onClose: () => void;
  /** The filing just added (an obligation's id or an own filing's), to open its panel. */
  onAdded: (filingId: string) => void;
  /** A filing chosen in advance, and the label it goes on with (the review's JQ2 offer). */
  preset?: AddFilingPreset;
}

/**
 * The review's "We file it" offers "Add a filing" with the filing chosen and its own label, "Added
 * by your organization" (D-CCM-37 JQ2 (A); frame 98). Givmo's result is never rewritten: the filing
 * goes on as the organization's own addition, through the same add as any other.
 */
export interface AddFilingPreset {
  jurisdictionCode: string;
  ruleId: string;
  label?: string;
}

export function AddFilingSheet({ open, onClose, onAdded, preset }: AddFilingSheetProps) {
  const [ownFiling, setOwnFiling] = useState(false);
  const close = () => {
    setOwnFiling(false);
    onClose();
  };
  const added = (filingId: string) => {
    setOwnFiling(false);
    onAdded(filingId);
  };
  return (
    <Sheet.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {open && (
        <Sheet.Content size="lg" testId={PREFIX} initialFocus={false}>
          <SheetTitle />
          {ownFiling ? (
            <OwnFilingForm onClose={close} onAdded={added} />
          ) : (
            <LibraryFilingForm
              onClose={close}
              onAdded={added}
              onNotListed={() => setOwnFiling(true)}
              {...(preset !== undefined ? { preset } : {})}
            />
          )}
        </Sheet.Content>
      )}
    </Sheet.Root>
  );
}
